import { useState, useEffect, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Modal, Box } from '@rsos/base-components';
import { ModalHeader } from '@rsos/components/capstone/base/Modal/ModalComponents';
import { fetchAgencyLinks } from '@rsos/reduxDataStore/src/features/peerAgencies/peerAgenciesSlice.js';
import { AppState } from '../../types/AppState';
import { TabLabel, TabLabelWrapper } from './ShareEvent.styles';
import { ShareEventButton, FieldShare } from './components';
import EccShare from './components/EccShare/EccShare';
import {
  CALL_RESOURCE,
  type ShareResource,
} from './constants/ShareEvent.types';
import {
  ECC_SHARE_METHOD,
  FIELD_SHARE_METHOD,
  type TabSharingMethod,
} from './constants/shareMethods';

interface Props {
  isLabelHidden?: boolean;
  /** What is being shared. Defaults to a call so existing mounts are unchanged. */
  resource?: ShareResource;
  renderTrigger?: (onClick: () => void) => ReactNode;
}

const ShareEvent = ({
  isLabelHidden = false,
  resource = CALL_RESOURCE,
  renderTrigger,
}: Props) => {
  // ECC share is call-only (it builds a `tel://` URI), so alert and resource
  // request shares open straight into Field Responders and the ECC tab is not
  // offered.
  const isCall = resource.kind === 'call';
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabSharingMethod>(
    isCall ? ECC_SHARE_METHOD : FIELD_SHARE_METHOD,
  );
  const dispatch = useDispatch();
  const orgName = useSelector(
    (state: AppState) => state.sinatra.user?.currentOrg?.name,
  );
  const handleCloseModal: () => void = () => {
    setIsOpen(false);
  };

  const handleClickShareButton = () => {
    if (isOpen) {
      handleCloseModal();
    } else {
      setIsOpen(true);
    }
  };

  const handleTabChange = (tab: TabSharingMethod) => {
    setActiveTab(tab);
  };
  useEffect(() => {
    if (orgName) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - TODO: Convert peerAgenciesSlice to TypeScript
      dispatch(fetchAgencyLinks(orgName));
    }
  }, [dispatch, orgName]);
  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleClickShareButton)
      ) : (
        <ShareEventButton
          onClick={handleClickShareButton}
          isLabelHidden={isLabelHidden}
        />
      )}
      <Modal
        isOpened={isOpen}
        onClose={handleCloseModal}
        width={425}
        id="modal-share-event"
        overflowY="auto"
      >
        <ModalHeader onClose={handleCloseModal}>
          <Text fontWeight="bold" size="large" color="primaryText">
            Share Event
          </Text>
        </ModalHeader>
        <Box mb={0}>
          <Box horizontal flow={16} mb="16px">
            {isCall && (
              <TabLabelWrapper
                onClick={() => handleTabChange(ECC_SHARE_METHOD)}
                isActive={activeTab === ECC_SHARE_METHOD}
              >
                <TabLabel isActive={activeTab === ECC_SHARE_METHOD}>
                  ECCs
                </TabLabel>
              </TabLabelWrapper>
            )}
            <TabLabelWrapper
              onClick={() => handleTabChange(FIELD_SHARE_METHOD)}
              isActive={activeTab === FIELD_SHARE_METHOD}
            >
              <TabLabel isActive={activeTab === FIELD_SHARE_METHOD}>
                Field Responders
              </TabLabel>
            </TabLabelWrapper>
          </Box>
          <Box>
            {isCall && activeTab === ECC_SHARE_METHOD && (
              <EccShare onClose={handleCloseModal} />
            )}
            {activeTab === FIELD_SHARE_METHOD && (
              <FieldShare onClose={handleCloseModal} resource={resource} />
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ShareEvent;
